import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { Badge, Button, Pagination, Skeleton, Table } from 'antd';

import { useGetAllReferencesQuery, useLazyGetAllReferencesQuery } from 'app/services/edpApiV1/references';

import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';

import { dataForReferencesTable, sortingDate } from 'shared/helpers/utils';

import ReferencesModalSubordinates from 'features/references/referencesModalSubordinates';
import { ColumnsType } from 'antd/lib/table';
import { usePullSort } from 'shared/hooks';
import { RolesUser, SortCondition } from 'shared/enums';
import { useIndicators } from 'features/indicators';
import { TPreparedReferencesTableData } from 'shared/types/reference.types';
import SearchComponent from 'shared/components/search/news-faq';
import { SearchKeysNews } from 'shared/enums/news';
import StringFormItem from 'shared/components/search/news-faq/stringFormItem/StringFormItem';
import NumberFormItem from 'shared/components/search/news-faq/numberFormItem/NumberFormItem';
import { RequestsSearch } from 'shared/types/types';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { viewerModel } from 'entities/viewer';
import style from './index.module.scss';

const ReferencesSubordinates = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataReferencesSubordinates, setDataReferencesSubordinates] = useState<TPreparedReferencesTableData[]>();
  const [openModal, setOpenModal] = useState(false);
  const [requestId, setRequestId] = useState<number>();
  const [referenceHrId, setReferenceHrId] = useState<number | null>(null);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [sortInfo, sortTrigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const navigate = useNavigate();
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const accountInfo = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });

  const [getReferences, { data, isLoading, isFetching, isSuccess }] = useLazyGetAllReferencesQuery();

  useEffect(() => {
    getReferences({
      type: 'subordinatesInWork',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getReferences, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  const handleChangeResponsible = (id: number, hrId: number): void => {
    setReferenceHrId(hrId);
    setRequestId(id);
    setOpenModal(true);
  };

  const handlePagination = (value: number, pageSize: number) => {
    setCurrentSize(pageSize);
    setCurrentPage(value);
  };
  const columns: ColumnsType<TPreparedReferencesTableData> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'Вид справки',
      dataIndex: 'refTypeName',
      key: 'refTypeName',
    },
    {
      title: 'Период',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Получение',
      dataIndex: 'gettingMethod',
      key: 'gettingMethod',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span className="d-flex">
          <Badge status={status.type} />
          {status.text}
        </span>
      ),
    },
  ];

  if (!allAvailableOrganizations && accountInfo.role === RolesUser.HRDirector) {
    columns.push({
      title: 'Ответственный',
      dataIndex: 'hr',
      key: 'hr',
      render: (hr, record: { key: React.Key }) => (
        <>
          {hr.name || hr.middleName || hr.lastName ? (
            <>
              <span>{`${hr.lastName} ${hr.name} ${hr.middleName ?? ''}`}</span>
              <EditOutlined
                className={style.btnChangeResponsible}
                onClick={() => handleChangeResponsible(Number(record.key), hr.id)}
              />
            </>
          ) : (
            <span
              className={style.changeResponsible}
              onClick={() => handleChangeResponsible(Number(record.key), hr.id)}
            >
              Назначить
            </span>
          )}
        </>
      ),
    });
  }

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      setRequestId(Number(selectedRowKeys));
      setShowDetails(true);
    },
  };

  useEffect(() => {
    if (data) {
      const dataTable = dataForReferencesTable(data?.data);
      if (dataTable) {
        setDataReferencesSubordinates(dataTable);
      }
    }
  }, [data]);

  useIndicators(isSuccess, isFetching);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  return (
    <>
      <SearchComponent
        selectOptions={{
          [SearchKeysNews.SEARCH_NAME]: {
            name: SearchKeysNews.SEARCH_NAME,
            alias: 'ФИО',
            node: StringFormItem({
              name: SearchKeysNews.SEARCH_NAME,
              placeholder: 'Введите ФИО',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_NAME);
              },
            }),
          },
          [SearchKeysNews.SEARCH_ID]: {
            name: SearchKeysNews.SEARCH_ID,
            alias: 'Номер заявки',
            node: NumberFormItem({
              name: SearchKeysNews.SEARCH_ID,
              placeholder: 'Введите номер заявки',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_ID);
              },
            }),
          },
          [SearchKeysNews.PERSONNEL_NUMBER]: {
            name: SearchKeysNews.PERSONNEL_NUMBER,
            alias: 'ТН',
            node: NumberFormItem({
              name: SearchKeysNews.PERSONNEL_NUMBER,
              placeholder: 'Введите ТН',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.PERSONNEL_NUMBER);
              },
            }),
          },
          [SearchKeysNews.POSITION_NAME]: {
            name: SearchKeysNews.POSITION_NAME,
            alias: 'Должность',
            node: NumberFormItem({
              name: SearchKeysNews.POSITION_NAME,
              placeholder: 'Введите должность',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.POSITION_NAME);
              },
            }),
          },
          [SearchKeysNews.DIVISION_NAME]: {
            name: SearchKeysNews.DIVISION_NAME,
            alias: 'Подразделение',
            node: StringFormItem({
              name: SearchKeysNews.DIVISION_NAME,
              placeholder: 'Введите подразделение',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.DIVISION_NAME);
              },
            }),
          },
        }}
      />
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          {showDetails && requestId && (
            <div className={style.subordinationBtnMore}>
              <Button type="default" onClick={() => navigate(`/references/user/subordination/${requestId}`)}>
                Подробнее
              </Button>
            </div>
          )}
          <Table
            scroll={{ x: true }}
            rowSelection={{
              type: 'radio',
              ...rowSelection,
            }}
            dataSource={dataReferencesSubordinates}
            columns={columns}
            pagination={false}
            bordered
            rowClassName={record => {
              if (record?.downtimeStatus === 'red') {
                return style.red;
              } else if (record?.downtimeStatus === 'yellow') {
                return style.yellow;
              }
              return '';
            }}
          />
          <div className={style.paginationWrap}>
            <Pagination
              defaultCurrent={1}
              current={currentPage}
              pageSize={currentSize}
              showSizeChanger
              total={data?.pagination?.total}
              onChange={(page, pageSize) => handlePagination(page, pageSize)}
            />
          </div>
          {allSubordinates && allSubordinates.length > 0 && requestId && (
            <ReferencesModalSubordinates
              openModal={openModal}
              setOpenModal={setOpenModal}
              requestId={requestId}
              referenceHrId={referenceHrId}
            />
          )}
        </>
      )}
    </>
  );
};

export default ReferencesSubordinates;
